import { Check, Copy, Pencil, Share2, Trash2, UserPlus, Users } from 'lucide-react';
import qrcode from 'qrcode-generator';
import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { canvasPart, CONTENT_LABELS, DEFAULT_INCLUDE, mergeCanvas, type ContentKey, type Include } from '../../lib/canvasContent';
import { endpoints, type Group, type SharedCanvas } from '../../services/api';
import { captureSnapshot, type Preset } from '../../store/presets';
import { useScreen } from '../../store/screen';
import { useSession } from '../../store/session';
import { newId, useSettings, withDefaults } from '../../store/settings';
import { buttonClass, ghostButtonClass, inputClass, Section, Toggle } from './controls';

// Settings → Sharing: groups (a family, an office) joined by invite link, and the canvases
// shared with them. See server/src/routes/groups.ts and canvases.ts for the rules.

function useGroups() {
  const [groups, setGroups] = useState<Group[] | null>(null);
  const load = useCallback(() => endpoints.groups().then((r) => setGroups(r.groups)).catch(() => setGroups([])), []);
  useEffect(() => void load(), [load]);
  return { groups, reload: load };
}

function useCanvases() {
  const [canvases, setCanvases] = useState<SharedCanvas[] | null>(null);
  const load = useCallback(() => endpoints.canvases().then((r) => setCanvases(r.canvases)).catch(() => setCanvases([])), []);
  useEffect(() => void load(), [load]);
  return { canvases, reload: load };
}

/** What this screen shows: a shared canvas ("shared:<id>"), a saved layout id, or null for the main setup. */
export function showOnThisScreen(target: string | null): void {
  const user = useSession.getState().user;
  if (!user) return;
  useScreen.getState().setAssignment(user.id, target);
  void endpoints.updateScreen(useScreen.getState().deviceId, { presetId: target }).catch(() => undefined);
}

export function SharingTab() {
  const { config, user } = useSession();
  const { groups, reload: reloadGroups } = useGroups();
  const { canvases, reload: reloadCanvases } = useCanvases();
  if (!config?.features.accounts) return <p className="muted text-sm">Sharing needs accounts, which aren’t switched on for this site yet.</p>;
  if (!user) return <p className="muted text-sm">Sign in (Settings → Account) to share canvases with your family or colleagues.</p>;
  const reload = () => {
    void reloadGroups();
    void reloadCanvases();
  };
  return (
    <>
      <GroupsSection groups={groups} onChanged={reload} />
      <CanvasesSection canvases={canvases} groups={groups ?? []} onChanged={reload} />
    </>
  );
}

// ---------------------------------------------------------------- groups

function GroupsSection({ groups, onChanged }: { groups: Group[] | null; onChanged: () => void }) {
  const [name, setName] = useState('');
  const create = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    await endpoints.createGroup(name.trim()).catch(() => undefined);
    setName('');
    onChanged();
  };
  return (
    <Section
      title="Groups"
      description="A group is your family, your office or anyone you want to share canvases with. Invite people with a link or QR code; they sign in with their own account."
    >
      {groups === null && <p className="muted text-sm">Loading…</p>}
      <ul className="space-y-2">
        {groups?.map((g) => (
          <GroupCard key={g.id} group={g} onChanged={onChanged} />
        ))}
      </ul>
      <form onSubmit={create} className="flex gap-2">
        <input
          className={inputClass}
          placeholder="New group, e.g. Kirkpatrick family or KCL office"
          value={name}
          maxLength={60}
          onChange={(e) => setName(e.target.value)}
          aria-label="New group name"
        />
        <button className={buttonClass} type="submit">
          <Users className="h-4 w-4" aria-hidden /> Create
        </button>
      </form>
    </Section>
  );
}

function InviteLink({ url, expiresAt }: { url: string; expiresAt: string }) {
  const [copied, setCopied] = useState(false);
  const qr = useMemo(() => {
    const q = qrcode(0, 'M');
    q.addData(url);
    q.make();
    return q.createDataURL(4, 2);
  }, [url]);
  return (
    <div className="flex items-start gap-3 rounded-lg bg-white/5 p-3">
      <img src={qr} alt="QR code for the invite link" className="h-28 w-28 rounded bg-white p-1" />
      <div className="min-w-0 flex-1 space-y-2 text-xs">
        <p>Scan the code, or send this link. It works for 7 days (until {new Date(expiresAt).toLocaleDateString()}).</p>
        <p className="muted break-all">{url}</p>
        <button
          type="button"
          className={ghostButtonClass}
          onClick={() => {
            void navigator.clipboard?.writeText(url).then(() => setCopied(true));
          }}
        >
          {copied ? <Check className="h-4 w-4" aria-hidden /> : <Copy className="h-4 w-4" aria-hidden />} {copied ? 'Copied' : 'Copy link'}
        </button>
      </div>
    </div>
  );
}

function GroupCard({ group: g, onChanged }: { group: Group; onChanged: () => void }) {
  const owner = g.role === 'owner';
  const [invite, setInvite] = useState<{ url: string; expiresAt: string } | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(g.name);
  const [confirm, setConfirm] = useState(false);
  const me = g.members.find((m) => m.me);
  return (
    <li className="space-y-2 rounded-lg border border-[var(--border)] p-3 text-sm">
      <div className="flex items-center gap-2">
        <Users className="h-4 w-4 shrink-0 opacity-80" aria-hidden />
        {renaming ? (
          <form
            className="flex-1"
            onSubmit={async (e) => {
              e.preventDefault();
              if (name.trim()) await endpoints.renameGroup(g.id, name.trim()).catch(() => undefined);
              setRenaming(false);
              onChanged();
            }}
          >
            <input className={inputClass} value={name} maxLength={60} autoFocus onChange={(e) => setName(e.target.value)} aria-label="Group name" />
          </form>
        ) : (
          <span className="min-w-0 flex-1 truncate font-medium">{g.name}</span>
        )}
        {owner && !renaming && (
          <button type="button" onClick={() => setRenaming(true)} aria-label={`Rename ${g.name}`} className="opacity-60 hover:opacity-100">
            <Pencil className="h-3.5 w-3.5" aria-hidden />
          </button>
        )}
        <span className="muted shrink-0 text-xs">
          {g.members.length} member{g.members.length === 1 ? '' : 's'}
        </span>
      </div>
      <ul className="space-y-1 pl-6">
        {g.members.map((m) => (
          <li key={m.userId} className="flex items-center gap-2 text-xs">
            <span className="min-w-0 flex-1 truncate">
              {m.name || m.email}
              {m.name && <span className="muted"> · {m.email}</span>}
              {m.me && <span className="muted"> (you)</span>}
            </span>
            {m.role === 'owner' && <span className="muted">Owner</span>}
            {owner && !m.me && (
              <button
                type="button"
                className="opacity-60 hover:opacity-100"
                aria-label={`Remove ${m.email}`}
                onClick={async () => {
                  await endpoints.removeMember(g.id, m.userId).catch(() => undefined);
                  onChanged();
                }}
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden />
              </button>
            )}
          </li>
        ))}
      </ul>
      {invite && <InviteLink url={invite.url} expiresAt={invite.expiresAt} />}
      <div className="flex flex-wrap gap-2">
        {owner && (
          <button type="button" className={ghostButtonClass} onClick={async () => setInvite(await endpoints.inviteToGroup(g.id))}>
            <UserPlus className="h-4 w-4" aria-hidden /> {invite ? 'New invite link' : 'Invite people'}
          </button>
        )}
        {owner ? (
          confirm ? (
            <button
              type="button"
              className={`${ghostButtonClass} border-red-400 text-red-400`}
              onClick={async () => {
                await endpoints.deleteGroup(g.id).catch(() => undefined);
                onChanged();
              }}
            >
              Delete the group for everyone?
            </button>
          ) : (
            <button type="button" className={ghostButtonClass} onClick={() => setConfirm(true)}>
              Delete group
            </button>
          )
        ) : (
          me && (
            <button
              type="button"
              className={ghostButtonClass}
              onClick={async () => {
                await endpoints.removeMember(g.id, me.userId).catch(() => undefined);
                onChanged();
              }}
            >
              Leave group
            </button>
          )
        )}
      </div>
    </li>
  );
}

// ---------------------------------------------------------------- shared canvases

function ContentChoices({ include, onChange, disabled }: { include: Include; onChange: (i: Include) => void; disabled?: boolean }) {
  return (
    <div className="space-y-2">
      <p className="muted text-xs">
        Always shared: the layout, look, widget settings, fonts and clock. Never shared: files added from your screens. Anything not
        shared comes from each person’s own setup. Your OneDrive, Outlook and To Do are shown to others through your Microsoft
        connection, which is never handed to anyone.
      </p>
      {(Object.keys(CONTENT_LABELS) as ContentKey[]).map((k) => (
        <div key={k}>
          {disabled ? (
            <p className="text-sm">
              {CONTENT_LABELS[k].name}: <span className="muted">{include[k] ? 'shared' : 'not shared'}</span>
            </p>
          ) : (
            <Toggle label={CONTENT_LABELS[k].name} checked={include[k]} onChange={(v) => onChange({ ...include, [k]: v })} />
          )}
          {include[k] && CONTENT_LABELS[k].warning && <p className="text-xs text-amber-400">{CONTENT_LABELS[k].warning}</p>}
        </div>
      ))}
    </div>
  );
}

/** Rights for one group: always "use", plus optional edit and share-on. */
function RightsPicker({
  rights,
  onChange,
  max,
}: {
  rights: { canEdit: boolean; canShare: boolean };
  onChange: (r: { canEdit: boolean; canShare: boolean }) => void;
  max: { edit: boolean; share: boolean };
}) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
      <label className="flex items-center gap-1.5">
        <input type="checkbox" checked disabled className="accent-[var(--accent)]" /> Use on their screens
      </label>
      <label className={`flex items-center gap-1.5 ${max.edit ? '' : 'opacity-50'}`}>
        <input
          type="checkbox"
          checked={rights.canEdit}
          disabled={!max.edit}
          onChange={(e) => onChange({ ...rights, canEdit: e.target.checked })}
          className="accent-[var(--accent)]"
        />
        Edit
      </label>
      <label className={`flex items-center gap-1.5 ${max.share ? '' : 'opacity-50'}`}>
        <input
          type="checkbox"
          checked={rights.canShare}
          disabled={!max.share}
          onChange={(e) => onChange({ ...rights, canShare: e.target.checked })}
          className="accent-[var(--accent)]"
        />
        Share with others
      </label>
    </div>
  );
}

function CanvasesSection({ canvases, groups, onChanged }: { canvases: SharedCanvas[] | null; groups: Group[]; onChanged: () => void }) {
  const mine = canvases?.filter((c) => c.owner.me) ?? [];
  const withMe = canvases?.filter((c) => !c.owner.me) ?? [];
  return (
    <>
      <Section
        title="Shared by you"
        description="To share a canvas, save it under Appearance → Saved layouts and press Share. Groups you share with can always use it; choose whether they can also edit it or share it on."
      >
        {canvases === null && <p className="muted text-sm">Loading…</p>}
        {canvases && mine.length === 0 && <p className="muted text-sm">You haven’t shared any canvases yet.</p>}
        <ul className="space-y-2">
          {mine.map((c) => (
            <CanvasCard key={c.id} canvas={c} groups={groups} onChanged={onChanged} />
          ))}
        </ul>
      </Section>
      <Section title="Shared with you" description="Canvases from people in your groups. Show one on any of your screens, or make your own copy to change it.">
        {canvases && withMe.length === 0 && <p className="muted text-sm">Nothing shared with you yet.</p>}
        <ul className="space-y-2">
          {withMe.map((c) => (
            <CanvasCard key={c.id} canvas={c} groups={groups} onChanged={onChanged} />
          ))}
        </ul>
      </Section>
    </>
  );
}

function CanvasCard({ canvas: c, groups, onChanged }: { canvas: SharedCanvas; groups: Group[]; onChanged: () => void }) {
  const user = useSession((s) => s.user);
  const assigned = useScreen((s) => (user ? s.assignments[user.id] : null));
  const onThisScreen = assigned === `shared:${c.id}`;
  const [include, setInclude] = useState<Include>(c.include);
  const [confirm, setConfirm] = useState(false);
  const [copied, setCopied] = useState(false);
  const [newGroup, setNewGroup] = useState('');
  const [newRights, setNewRights] = useState({ canEdit: false, canShare: false });
  const p = c.permissions;
  const notShared = groups.filter((g) => !c.shares.some((s) => s.groupId === g.id));

  const makeCopy = async () => {
    const full = (await endpoints.canvas(c.id)).canvas;
    const s = useSettings.getState();
    const merged = mergeCanvas(full.snapshot ?? {}, s, full.include);
    const preset: Preset = { id: newId(), name: `${c.name} (copy)`.slice(0, 60), savedAt: new Date().toISOString(), snapshot: captureSnapshot(merged) };
    s.update('presets', [...s.presets, preset]);
    setCopied(true);
  };

  return (
    <li className="space-y-3 rounded-lg border border-[var(--border)] p-3 text-sm">
      <div className="flex items-center gap-2">
        <Share2 className="h-4 w-4 shrink-0 opacity-80" aria-hidden />
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium">{c.name}</div>
          <div className="muted truncate text-xs">
            {p.owner ? 'Yours' : `From ${c.owner.name || c.owner.email}`} · {p.edit ? 'you can edit it' : 'view only'}
            {p.share && !p.owner ? ' · you can share it' : ''}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className={onThisScreen ? ghostButtonClass : buttonClass}
          onClick={() => showOnThisScreen(onThisScreen ? null : `shared:${c.id}`)}
        >
          {onThisScreen ? 'Stop showing on this screen' : 'Show on this screen'}
        </button>
        <button type="button" className={ghostButtonClass} onClick={() => void makeCopy()}>
          {copied ? <Check className="h-4 w-4" aria-hidden /> : null} {copied ? 'Copied to Saved layouts' : 'Make my own copy'}
        </button>
      </div>

      {(p.owner || p.share) && (
        <div className="space-y-2 border-t border-[var(--border)] pt-3">
          <div className="text-xs font-semibold uppercase tracking-wider opacity-80">Shared with</div>
          {c.shares.length === 0 && <p className="muted text-xs">No groups yet.</p>}
          {c.shares.map((s) => {
            const mayChange = p.owner || s.mine;
            return (
              <div key={s.groupId} className="space-y-1 rounded-md bg-white/5 p-2">
                <div className="flex items-center gap-2">
                  <span className="min-w-0 flex-1 truncate">{s.groupName}</span>
                  {mayChange && (
                    <button
                      type="button"
                      className="text-xs opacity-70 hover:opacity-100 hover:underline"
                      onClick={async () => {
                        await endpoints.unshareCanvas(c.id, s.groupId).catch(() => undefined);
                        onChanged();
                      }}
                    >
                      Stop sharing
                    </button>
                  )}
                </div>
                {mayChange ? (
                  <RightsPicker
                    rights={{ canEdit: s.canEdit, canShare: s.canShare }}
                    max={{ edit: p.edit, share: p.share }}
                    onChange={async (r) => {
                      await endpoints.shareCanvas(c.id, s.groupId, r).catch(() => undefined);
                      onChanged();
                    }}
                  />
                ) : (
                  <p className="muted text-xs">
                    Use{s.canEdit ? ' · edit' : ''}
                    {s.canShare ? ' · share' : ''}
                  </p>
                )}
              </div>
            );
          })}
          {p.share && notShared.length > 0 && (
            <div className="space-y-2 rounded-md border border-dashed border-[var(--border)] p-2">
              <select className={`${inputClass} bg-[var(--surface-strong)]`} value={newGroup} onChange={(e) => setNewGroup(e.target.value)} aria-label="Share with group">
                <option value="">Share with another group…</option>
                {notShared.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
              {newGroup && (
                <>
                  <RightsPicker rights={newRights} onChange={setNewRights} max={{ edit: p.edit, share: p.share }} />
                  <button
                    type="button"
                    className={buttonClass}
                    onClick={async () => {
                      await endpoints.shareCanvas(c.id, newGroup, newRights).catch(() => undefined);
                      setNewGroup('');
                      setNewRights({ canEdit: false, canShare: false });
                      onChanged();
                    }}
                  >
                    Share
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {p.owner && (
        <details className="border-t border-[var(--border)] pt-3">
          <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wider opacity-80">What’s included</summary>
          <div className="mt-2 space-y-2">
            <ContentChoices
              include={include}
              onChange={async (i) => {
                setInclude(i);
                await endpoints.updateCanvas(c.id, { include: { ...i } }).catch(() => undefined);
              }}
            />
            {confirm ? (
              <button
                type="button"
                className={`${ghostButtonClass} border-red-400 text-red-400`}
                onClick={async () => {
                  await endpoints.deleteCanvas(c.id).catch(() => undefined);
                  if (onThisScreen) showOnThisScreen(null);
                  onChanged();
                }}
              >
                Delete it for everyone?
              </button>
            ) : (
              <button type="button" className={ghostButtonClass} onClick={() => setConfirm(true)}>
                <Trash2 className="h-4 w-4" aria-hidden /> Delete shared canvas
              </button>
            )}
          </div>
        </details>
      )}
    </li>
  );
}

/** Saved layouts → Share: turns a saved layout into a shared canvas and shares it with groups. */
export function ShareLayoutForm({ preset, onDone }: { preset: Preset; onDone: () => void }) {
  const { groups } = useGroups();
  const [include, setInclude] = useState<Include>(DEFAULT_INCLUDE);
  const [picked, setPicked] = useState<Record<string, { canEdit: boolean; canShare: boolean }>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const share = async () => {
    if (!preset.snapshot) return;
    setBusy(true);
    setError(null);
    try {
      const settings = withDefaults({ ...preset.snapshot, presets: [] });
      const { canvas } = await endpoints.createCanvas({
        name: preset.name,
        snapshot: canvasPart(settings, include),
        include: { ...include },
        fromPresetId: preset.id,
      });
      for (const [groupId, rights] of Object.entries(picked)) await endpoints.shareCanvas(canvas.id, groupId, rights);
      // This screen showed the saved layout: it now shows the shared canvas.
      const user = useSession.getState().user;
      if (user && useScreen.getState().assignments[user.id] === preset.id) useScreen.getState().setAssignment(user.id, `shared:${canvas.id}`);
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not share it');
      setBusy(false);
    }
  };

  if (!preset.snapshot) return <p className="muted text-xs">This layout was saved in an older format. Apply it and save it again to share it.</p>;
  return (
    <div className="space-y-3 rounded-lg border border-[var(--accent)]/50 bg-[var(--accent)]/5 p-3">
      <div className="text-sm font-medium">Share “{preset.name}”</div>
      <div className="space-y-2">
        <div className="text-xs font-semibold uppercase tracking-wider opacity-80">With</div>
        {groups === null && <p className="muted text-xs">Loading your groups…</p>}
        {groups?.length === 0 && <p className="muted text-xs">Create a group first in Settings → Sharing, then invite people to it.</p>}
        {groups?.map((g) => {
          const on = Boolean(picked[g.id]);
          return (
            <div key={g.id} className="space-y-1">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={on}
                  onChange={(e) =>
                    setPicked((p) => {
                      const next = { ...p };
                      if (e.target.checked) next[g.id] = { canEdit: false, canShare: false };
                      else delete next[g.id];
                      return next;
                    })
                  }
                  className="accent-[var(--accent)]"
                />
                {g.name}
              </label>
              {on && (
                <div className="pl-6">
                  <RightsPicker rights={picked[g.id]!} onChange={(r) => setPicked((p) => ({ ...p, [g.id]: r }))} max={{ edit: true, share: true }} />
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="space-y-2">
        <div className="text-xs font-semibold uppercase tracking-wider opacity-80">Included</div>
        <ContentChoices include={include} onChange={setInclude} />
      </div>
      <p className="muted text-xs">Your saved layout stays as it is. Your screens that showed it will show the shared version, which updates for everyone.</p>
      {error && <p className="text-sm text-red-400">{error}</p>}
      <div className="flex gap-2">
        <button type="button" className={buttonClass} disabled={busy || Object.keys(picked).length === 0} onClick={() => void share()}>
          <Share2 className="h-4 w-4" aria-hidden /> Share
        </button>
        <button type="button" className={ghostButtonClass} onClick={onDone}>
          Cancel
        </button>
      </div>
    </div>
  );
}
